import React, { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useProfile } from "../../hooks/useProfile";
import { apiFetch } from "../../context/AuthContext";
import { useAuth } from "../../context/AuthContext";

// Constants
import translations from "./constants/translations";
import getEventTypes from "./constants/eventTypes";

// Hooks
import useResponsive from "./hooks/useResponsive";
import useCalendarNavigation from "./hooks/useCalendarNavigation";
import useCalendarEvents from "./hooks/useCalendarEvents";
import useCalendarStats from "./hooks/useCalendarStats";
import useCalendarExport from "./hooks/useCalendarExport";
import useEmployees from "./hooks/useEmployees";
import useTodos from "./hooks/useTodos";

// Styles
import getResponsiveStyles from "./styles/calendarStyles";

// Utils
import { formatDate } from "./utils/dateUtils";
import { getShortName } from "./utils/nameUtils";
import { validateEmails } from "./utils/emailValidator";

// Components - Layout
import CalendarHeader from "./components/layout/CalendarHeader";

// Components - Stats
import StatsGrid from "./components/stats/StatsGrid";

// Components - Calendar
import CalendarMain from "./components/calendar/CalendarMain";

// Components - Sidebar
import EventTypesList from "./components/sidebar/EventTypesList";
import UpcomingEvents from "./components/sidebar/UpcomingEvents";

// Components - Todo
import SimpleTodoList from "./components/todo/SimpleTodoList";

// Components - Modals
import EventModal from "./components/modals/EventModal/EventModal";
import ExportModal from "./components/modals/ExportModal";
import ShareModal from "./components/modals/ShareModal";
import DayEventsModal from "./components/modals/DayEventsModal";

const Calendar = ({
  currentInfinityCode: _currentInfinityCodeProp = null,
}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const t = { ...translations.en, ...(translations[language] || {}) };
  const { hasFeatureAccess } = useAuth();

  const { profile } = useProfile();
  const currentInfinityCode =
    _currentInfinityCodeProp ??
    profile?.infinityCode ??
    localStorage.getItem("infinityCode");

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const API_BASE = `${BASE_URL}/calendar`;

  // Hooks
  const screenSize = useResponsive();
  const styles = getResponsiveStyles(screenSize, theme, language);

  const {
    currentDate,
    setCurrentDate,
    selectedDate,
    setSelectedDate,
    handlePrevMonth,
    handleNextMonth,
    getDaysInMonth,
  } = useCalendarNavigation();

  const eventTypes = getEventTypes(t, language);

  const {
    events,
    setEvents,
    loading,
    errorMsg,
    setErrorMsg,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    updateRSVP,
    getEventsForDate,
    getUpcomingEvents,
  } = useCalendarEvents(API_BASE, currentInfinityCode, eventTypes, t);

  const { calendarStats, updateCalendarStats } = useCalendarStats();

  const {
    employeeList,
    fetchAllEmployees,
    loadOriginatorName,
  } = useEmployees(API_BASE, t);

  const {
    todos,
    setTodos,
    addTodo: addTodoAPI,
    toggleTodo,
    deleteTodo,
    getFilteredTodos,
    getTodoStats,
  } = useTodos(BASE_URL, currentInfinityCode);

  // Local State
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [vacationDates, setVacationDates] = useState(new Set());
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRangeMode, setDateRangeMode] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);

  // Modal State
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [dayEventsPopup, setDayEventsPopup] = useState(null);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exportDateRange, setExportDateRange] = useState("this_month");

  // Form State
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState("");

  // Todo State
  const [newTodoText, setNewTodoText] = useState("");
  const [todoFilter, setTodoFilter] = useState("all");
  const [todoCategory, setTodoCategory] = useState("personal");

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [notificationSettings] = useState({
    emailReminders: true,
    pushNotifications: true,
    weeklyDigest: true,
    eventUpdates: true,
    vacationAlerts: true,
  });

  const emptyForm = {
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    time: "09:00",
    duration: 60,
    type: "meeting",
    location: "",
    attendees: "",
    priority: "medium",
    status: "scheduled",
    reminder: false,
    recurrenceRule: "",
    infinityCode: currentInfinityCode || null,
    id: null,
  };

  const [formData, setFormData] = useState(emptyForm);

  // Export hook (needs monthNames)
  const monthNames =
    language === "ar"
      ? [t.january, t.february, t.march, t.april, t.may, t.june, t.july, t.august, t.september, t.october, t.november, t.december]
      : ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const dayNames =
    language === "ar"
      ? [t.sun, t.mon, t.tue, t.wed, t.thu, t.fri, t.sat]
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const sendNotification = (notification) => {
    setNotifications((prev) => [
      { id: Date.now(), ...notification, time: new Date(), read: false },
      ...prev,
    ]);
  };

  const { exportCalendar, createCalendarCanvas } = useCalendarExport(
    currentDate,
    monthNames,
    language,
    sendNotification
  );

  // Effects
  useEffect(() => {
    if (language === "ar") {
      document.dir = "rtl";
      document.documentElement.dir = "rtl";
      document.documentElement.lang = "ar";
    } else {
      document.dir = "ltr";
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
    }
  }, [language]);

  // Notification deep-link (?eventId=...): once events load, open that
  // event's details modal directly.
  const [searchParams] = useSearchParams();
  const focusEventConsumedRef = useRef(false);

  useEffect(() => {
    fetchEvents().then((uiEvents) => {
      if (uiEvents) {
        updateCalendarStats(uiEvents, vacationDates);
        const focusEventId = searchParams.get("eventId");
        if (focusEventId && !focusEventConsumedRef.current) {
          const target = uiEvents.find((e) => String(e.id) === String(focusEventId));
          if (target) {
            focusEventConsumedRef.current = true;
            openEditModal(target);
          }
        }
      }
    });
    fetchAllEmployees();
  }, [currentInfinityCode]);

  // Handlers
  const handleCardHover = (e, isEntering) => {
    if (isEntering) {
      Object.assign(e.currentTarget.style, {
        transform: "translateY(-8px) scale(1.02)",
        boxShadow:
          theme === "dark"
            ? "0 32px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)"
            : "0 32px 64px rgba(0,0,0,0.2)",
      });
    } else {
      Object.assign(e.currentTarget.style, {
        transform: "translateY(0) scale(1)",
        boxShadow:
          theme === "dark"
            ? "0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 20px 40px rgba(0,0,0,0.12)",
      });
    }
  };

  const handleButtonHover = (e, isEntering, variant = "default") => {
    if (isEntering) {
      switch (variant) {
        case "primary":
          Object.assign(e.currentTarget.style, {
            transform: "translateY(-3px) scale(1.05)",
            boxShadow: "0 15px 40px rgba(59, 130, 246, 0.5)",
          });
          break;
        case "success":
          Object.assign(e.currentTarget.style, {
            transform: "translateY(-3px) scale(1.05)",
            boxShadow: "0 15px 40px rgba(16, 185, 129, 0.5)",
          });
          break;
        default:
          Object.assign(e.currentTarget.style, {
            transform: "translateY(-2px) scale(1.03)",
            background:
              theme === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
          });
      }
    } else {
      Object.assign(e.currentTarget.style, {
        transform: "translateY(0) scale(1)",
        boxShadow:
          variant === "default"
            ? "none"
            : variant === "primary"
            ? "0 8px 25px rgba(59, 130, 246, 0.4)"
            : "0 8px 25px rgba(16, 185, 129, 0.4)",
        background:
          variant === "default"
            ? theme === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.06)"
            : undefined,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateClick = (date) => {
    const canCreateEvent = hasFeatureAccess("calendar", "add");
    if (!canCreateEvent) {
      setErrorMsg(language === "ar" 
        ? "ليس لديك صلاحية لإنشاء حدث جديد" 
        : "You don't have permission to create a new event");
      return;
    }
    if (!date) return;
    const localDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const dateString = formatDate(localDate);
    setSelectedDate(localDate);
    setSelectedDates(new Set([dateString]));
    openCreateModal(localDate);
  };

  const handleShowMoreEvents = (date, dayEvents) => {
    setDayEventsPopup({ date, events: dayEvents });
  };

  const handleDatePickerSelect = (date) => {
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    setCurrentDate(new Date(localDate.getFullYear(), localDate.getMonth(), 1));
    setSelectedDate(localDate);
    setSelectedDates(new Set([formatDate(localDate)]));
  };

  const openCreateModal = async (date = new Date()) => {
    const canCreateEvent = hasFeatureAccess("calendar", "add");
    if (!canCreateEvent) {
      setErrorMsg(language === "ar" 
        ? "ليس لديك صلاحية لإنشاء حدث جديد" 
        : "You don't have permission to create a new event");
      return;
    }
    const d = formatDate(date);
    const currentUserCode = currentInfinityCode || localStorage.getItem("infinityCode");
    const originatorName = await loadOriginatorName(currentUserCode);

    const fallbackName =
      originatorName === t.unknownUser
        ? profile?.employeeName || localStorage.getItem("userName") || originatorName
        : originatorName;

    const currentUserEmail =
      profile?.employeeEmail ||
      localStorage.getItem("userEmail") ||
      (employeeList.find(
        (e) => String(e.infinityCode) === String(currentUserCode)
      ) || {}).email ||
      "";

    setFormData({
      ...emptyForm,
      date: d,
      originatorName: fallbackName,
      originatorCode: currentUserCode || localStorage.getItem("infinityCode") || null,
      isOriginator: true,
      attendees: currentUserEmail || "",
    });
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const openEditModal = async (uiEvent) => {
    setSelectedEvent(uiEvent);

    const currentUserCode = currentInfinityCode || localStorage.getItem("infinityCode");
    const eventOriginatorCode = uiEvent.originatorCode || null;
    const isOriginator =
      eventOriginatorCode &&
      currentUserCode &&
      parseInt(eventOriginatorCode) === parseInt(currentUserCode);

    let originatorName = t.unknownUser;
    if (eventOriginatorCode) {
      originatorName = await loadOriginatorName(eventOriginatorCode);
    }

    const currentUserEmail =
      profile?.employeeEmail ||
      localStorage.getItem("userEmail") ||
      (employeeList.find(
        (e) => String(e.infinityCode) === String(currentUserCode)
      ) || {}).email ||
      "";

    setFormData({
      title: uiEvent.title,
      description: uiEvent.description,
      date: uiEvent.date,
      time: uiEvent.time,
      duration: uiEvent.duration,
      type: uiEvent.type,
      location: uiEvent.location,
      attendees: uiEvent.attendees || "",
      priority: uiEvent.priority || "medium",
      status: uiEvent.status || "scheduled",
      reminder: uiEvent.reminder || false,
      recurrenceRule: uiEvent.raw?.recurrenceRule || "",
      // The event's true owner — never the current viewer — so saving an
      // edit can't silently reassign organizership to whoever clicked save.
      infinityCode: eventOriginatorCode ?? currentInfinityCode ?? null,
      id: uiEvent.id,
      originatorName: originatorName,
      originatorCode: eventOriginatorCode,
      isOriginator: isOriginator,
      ...(isOriginator && !uiEvent.attendees?.trim()
        ? { attendees: currentUserEmail }
        : {}),
    });

    setShowEventModal(true);
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    // Editing an existing event requires ownership AND the "calendar.edit"
    // feature (Enter-key form submission bypasses the hidden button otherwise).
    if (formData.id && !(formData.isOriginator && hasFeatureAccess("calendar", "edit"))) {
      setErrorMsg(language === "ar"
        ? "ليس لديك صلاحية لتعديل هذا الحدث"
        : "You don't have permission to edit this event");
      return;
    }
    try {
      if (formData.id) {
        await updateEvent(formData.id, formData);
      } else {
        await createEvent(formData);
      }
      setShowEventModal(false);
      const uiEvents = await fetchEvents();
      if (uiEvents) updateCalendarStats(uiEvents, vacationDates);

      if (notificationSettings.eventUpdates) {
        sendNotification({
          title: formData.id ? t.eventUpdated : t.eventCreated,
          message: `${formData.title} ${
            formData.id ? t.hasBeenUpdated : t.hasBeenCreated
          }`,
          type: "success",
        });
      }
    } catch (err) {
      console.error("Failed to create/update event:", err);
      setErrorMsg(err?.response?.data?.message || t.failedToSave);
    }
  };

  const handleStatusUpdate = async (eventId, newStatus) => {
    if (!currentInfinityCode || !eventId) {
      setErrorMsg(t.missingInfo);
      return;
    }
    try {
      const data = await updateRSVP(eventId, newStatus);
      setShowEventModal(false);
      const uiEvents = await fetchEvents();
      if (uiEvents) updateCalendarStats(uiEvents, vacationDates);

      // The RSVP endpoint returns the updated attendee record on success
      // (no `success` flag) and `{ success: false, message }` on failure —
      // so "no error message" is what actually signals success here.
      if (!data?.message) {
        sendNotification({
          title: t.rsvpUpdated,
          message: `${t.responseUpdated} "${t[newStatus] || newStatus}"`,
          type: "success",
        });
      } else {
        setErrorMsg(data.message);
      }
    } catch (err) {
      console.error("RSVP update failed:", err);
      setErrorMsg(
        err?.response?.data?.message || err?.response?.data?.error || t.failedToUpdate
      );
    }
  };

  const handleDelete = async (uiEvent) => {
    if (!uiEvent || !uiEvent.id) return;
    const issuerCode = uiEvent.originatorCode || uiEvent.infinityCode || "";
    const isIssuer = String(issuerCode) === String(currentInfinityCode);
    if (!isIssuer) {
      if (!window.confirm(`${t.deleteConfirm} "${uiEvent.title}"?`)) return;
    }
    try {
      await deleteEvent(uiEvent.id);
      setShowEventModal(false);
      if (notificationSettings.eventUpdates) {
        sendNotification({
          title: t.eventDeleted,
          message: `${uiEvent.title} ${t.hasBeenDeleted}`,
          type: "info",
        });
      }
    } catch (err) {
      console.error("Failed to delete event:", err);
      setErrorMsg(t.failedToDelete);
    }
  };

  const getCurrentUserRSVPStatus = (event) => {
    if (!currentInfinityCode || !event?.raw?.attendees) return null;
    const currentUserCode = String(currentInfinityCode);
    const attendee = event.raw.attendees.find(
      (att) =>
        att.employeeId === currentUserCode ||
        att.employee?.infinityCode === currentUserCode
    );
    return attendee ? attendee.status : null;
  };

  const addEmployeeFromSuggestion = (employee) => {
    const currentAttendees = formData.attendees || "";
    const parts = currentAttendees
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    parts.push(employee.email);
    const newValue = parts.join(", ") + ", ";
    setFormData((prev) => ({ ...prev, attendees: newValue }));
    setShowSuggestions(true);
    setSuggestionFilter("");
    setTimeout(() => {
      document.getElementById("attendees-input")?.focus();
    }, 0);
  };

  const addTodo = async (e) => {
    await addTodoAPI(e, newTodoText, todoCategory, setNewTodoText);
  };

  const filteredUpcomingEvents = getUpcomingEvents(filterType, searchTerm);
  const days = getDaysInMonth();

  const isFormOriginator = formData.isOriginator;

  return (
    <div>
      {/* Loading */}
      {loading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingSpinner} />
        </div>
      )}

      {/* Header */}
      <CalendarHeader
        t={t}
        styles={styles}
        screenSize={screenSize}
        language={language}
        onNewEvent={() => openCreateModal()}
        onDownloadImage={() => exportCalendar(false)}
        onDownloadPdf={() => exportCalendar(true)}
        onShare={() => setShowShareModal(true)}
        canAddEvent={hasFeatureAccess("calendar", "add")}
        canExportCalendar={hasFeatureAccess("calendar", "exportCalendar")}
        canShareCalendar={hasFeatureAccess("calendar", "shareCalendar")}
      />

      {/* Stats */}
      <StatsGrid
        calendarStats={calendarStats}
        screenSize={screenSize}
        language={language}
        theme={theme}
        t={t}
      />

      {/* Middle Section: Upcoming Events + Todo — side by side on desktop,
          stacked on phones (fixed "1fr 1fr" used to overflow the viewport). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
          gap: "24px",
          marginBottom: "10px",
        }}
      >
        <UpcomingEvents
          filteredUpcomingEvents={filteredUpcomingEvents}
          loading={loading}
          styles={styles}
          screenSize={screenSize}
          language={language}
          theme={theme}
          t={t}
          onEventClick={openEditModal}
        />

        {hasFeatureAccess("calendar", "manageTodos") && (
          <div style={styles.controlPanel}>
            <SimpleTodoList
              todos={todos}
              setTodos={setTodos}
              newTodoText={newTodoText}
              setNewTodoText={setNewTodoText}
              todoFilter={todoFilter}
              setTodoFilter={setTodoFilter}
              todoCategory={todoCategory}
              setTodoCategory={setTodoCategory}
              theme={theme}
              language={language}
              t={t}
              styles={styles}
              screenSize={screenSize}
              addTodo={addTodo}
              deleteTodo={deleteTodo}
              toggleTodo={toggleTodo}
            />
          </div>
        )}
      </div>

      {/* Calendar + Sidebar */}
      <div style={styles.calendarContainer}>
        <CalendarMain
          currentDate={currentDate}
          monthNames={monthNames}
          dayNames={dayNames}
          days={days}
          selectedDates={selectedDates}
          vacationDates={vacationDates}
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          dateRangeMode={dateRangeMode}
          getEventsForDate={(date) => getEventsForDate(date, filterType)}
          styles={styles}
          screenSize={screenSize}
          language={language}
          theme={theme}
          t={t}
          handlePrevMonth={handlePrevMonth}
          handleNextMonth={handleNextMonth}
          handleButtonHover={handleButtonHover}
          onDateClick={handleDateClick}
          onEventClick={openEditModal}
          onSelectMonth={handleDatePickerSelect}
          onShowMoreEvents={handleShowMoreEvents}
        />

        {/* <div style={styles.sidebar}>
          <EventTypesList
            eventTypes={eventTypes}
            styles={styles}
            screenSize={screenSize}
            language={language}
            theme={theme}
            onTypeClick={(type) => {
              if (type.id === "event") {
                openCreateModal();
              } else {
                setFilterType(type.id);
              }
            }}
          />
        </div> */}
      </div>

      {/* Modals */}
      <EventModal
        show={showEventModal}
        selectedEvent={selectedEvent}
        formData={formData}
        setFormData={setFormData}
        isOriginator={isFormOriginator}
        eventTypes={eventTypes}
        employeeList={employeeList}
        suggestionFilter={suggestionFilter}
        setSuggestionFilter={setSuggestionFilter}
        setShowSuggestions={setShowSuggestions}
        addEmployeeFromSuggestion={addEmployeeFromSuggestion}
        getCurrentUserRSVPStatus={getCurrentUserRSVPStatus}
        errorMsg={errorMsg}
        t={t}
        language={language}
        theme={theme}
        styles={styles}
        screenSize={screenSize}
        getShortName={getShortName}
        onSubmit={handleCreateOrUpdate}
        onClose={() => setShowEventModal(false)}
        onDelete={handleDelete}
        onStatusUpdate={handleStatusUpdate}
        handleButtonHover={handleButtonHover}
        handleInputChange={handleInputChange}
      />

      <ExportModal
        show={showExportModal}
        exportFormat={exportFormat}
        setExportFormat={setExportFormat}
        exportDateRange={exportDateRange}
        setExportDateRange={setExportDateRange}
        loading={loading}
        t={t}
        styles={styles}
        screenSize={screenSize}
        theme={theme}
        onClose={() => setShowExportModal(false)}
        onExport={() => {
          setShowExportModal(false);
        }}
        handleCardHover={handleCardHover}
        handleButtonHover={handleButtonHover}
      />

      <DayEventsModal
        show={!!dayEventsPopup}
        date={dayEventsPopup?.date}
        events={dayEventsPopup?.events || []}
        styles={styles}
        screenSize={screenSize}
        theme={theme}
        language={language}
        t={t}
        onClose={() => setDayEventsPopup(null)}
        onEventClick={(ev) => {
          setDayEventsPopup(null);
          openEditModal(ev);
        }}
      />

      <ShareModal
        show={showShareModal}
        currentDate={currentDate}
        monthNames={monthNames}
        language={language}
        t={t}
        styles={styles}
        screenSize={screenSize}
        theme={theme}
        onClose={() => setShowShareModal(false)}
        sendNotification={sendNotification}
        createCalendarCanvas={createCalendarCanvas}
        handleButtonHover={handleButtonHover}
      />

      <style jsx>{`
        div[style*="overflowY: auto"]::-webkit-scrollbar {
          display: none;
        }
        select option {
          background: ${theme === "dark"
            ? "rgba(2, 38, 97, 0.9)"
            : "#1715247e"} !important;
          color: #fff !important;
          padding: 12px;
        }
        select option:hover {
          background: ${theme === "dark"
            ? "rgba(2, 38, 97, 0.9)"
            : "#3a39457e"} !important;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .event-pill:hover {
          transform: translateY(-2px) scale(1.05);
          filter: brightness(1.1) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
        }
        @media (max-width: 768px) {
          body { -webkit-overflow-scrolling: touch; }
          input, select, textarea { font-size: 16px; }
        }
      `}</style>
    </div>
  );
};

export default Calendar;
